import Clarity from "@microsoft/clarity";

let initialized = false;

const isAnalyticsEnabled = (): boolean =>
  process.env.REACT_APP_FEATURE_ANALYTICS === "true";

const getProjectId = (): string | undefined => {
  const id = process.env.REACT_APP_CLARITY_PROJECT_ID?.trim();
  return id || undefined;
};

export const isClarityReady = (): boolean => initialized;

/**
 * Initialize Clarity once when analytics is enabled and a project ID is set.
 * Safe to call multiple times; subsequent calls are no-ops.
 */
export const initClarity = (): void => {
  if (initialized) return;
  if (!isAnalyticsEnabled()) return;

  const projectId = getProjectId();
  if (!projectId) return;

  Clarity.init(projectId);
  initialized = true;

  const environment =
    process.env.REACT_APP_ENVIRONMENT?.trim() || process.env.NODE_ENV;
  if (environment) {
    Clarity.setTag("environment", environment);
  }
};

export type IdentifyClarityUserParams = {
  /** Employee id when logged in; cookie UUID when anonymous. */
  userId: string;
  /** Persisted Clarity visitor session UUID (`c_sessionId`). */
  sessionId?: string;
  pageId?: string;
  friendlyName?: string;
};

export const identifyClarityUser = ({
  userId,
  sessionId,
  pageId,
  friendlyName
}: IdentifyClarityUserParams): void => {
  if (!initialized) return;
  Clarity.identify(userId, sessionId, pageId, friendlyName);
};

export const setClarityTags = (
  tags: Record<string, string | string[] | undefined | null>
): void => {
  if (!initialized) return;
  for (const [key, value] of Object.entries(tags)) {
    if (value === undefined || value === null || value === "") continue;
    Clarity.setTag(key, value);
  }
};

export const trackClarityEvent = (eventName: string): void => {
  if (!initialized) return;
  Clarity.event(eventName);
};

export const upgradeClaritySession = (reason: string): void => {
  if (!initialized) return;
  Clarity.upgrade(reason);
};

/** Derive a coarse route area for filtering sessions in Clarity. */
export const getClarityRouteArea = (pathname: string): string => {
  const path = pathname.toLowerCase();
  if (path.includes("/portal") && path.includes("/education")) {
    return "education";
  }
  if (path.includes("/membership") || path.includes("/subscription")) {
    return "membership";
  }
  if (path.includes("/portal")) {
    return "portal";
  }
  if (
    path.includes("/login") ||
    path.includes("/register") ||
    path.includes("/registration") ||
    path.includes("/verify") ||
    path.includes("/change-password") ||
    path.includes("/forgot-password") ||
    path.includes("/reset-password")
  ) {
    return "auth";
  }
  return "marketing";
};
